import { fx } from '../utilities/animation-preferences';

export default {
    logo: null,
    baseline: null,
    searchForm: null,
    _baselineParts: [],
    _applyInnerStroke: () => {},

    init() {
        this.logo ||= document.querySelector('.hero-slogan svg');
        this.baseline ||= document.querySelector('.hero-slogan p');
        this.searchForm ||= document.querySelector('.hero-search');

        // Préparation des tracés du logo (stroke dash)
        if (this.logo) {
            // Prépare les clipPaths pour un stroke "interne"
            this._applyInnerStroke = this.__ensureInnerStroke(this.logo);

            const shapes = this.logo.querySelectorAll(
                'path, line, polyline, polygon, rect, circle, ellipse'
            );

            if (!shapes.length) return;

            shapes.forEach((el) => {
                const len = typeof el.getTotalLength === 'function' ? el.getTotalLength() : 0;
                // Style commun
                fx.set(el, { stroke: '#fff', strokeWidth: '1px', fill: 'transparent', opacity: 1 });
                if (len > 0 && isFinite(len)) {
                    fx.set(el, { strokeDasharray: len, strokeDashoffset: len });
                    el.dataset.strokeLen = String(len);
                } else {
                    delete el.dataset.strokeLen; // pas d’anim dash pour cet élément
                }
            });
        }

        // Baseline → split par mots (GSAP SplitText si dispo, sinon fallback)
        if (this.baseline) {
            fx.set(this.baseline, { opacity: 0, y: -16 });
        }

        // Formulaire (inchangé)
        if (this.searchForm) fx.set(this.searchForm, { width: 72 });
    },

    load() {
        const tl = fx.timeline({ defaults: { ease: 'power2.out' } });

        // 1) Logo : dessiner les traits
        if (this.logo) {
            const allShapes = this.logo.querySelectorAll(
                'path, line, polyline, polygon, rect, circle, ellipse'
            );
            const dashShapes = Array.from(allShapes).filter(
                (el) => el.dataset && el.dataset.strokeLen
            );

            if (dashShapes.length) {
                tl.to(dashShapes, { strokeDashoffset: 0, duration: 0.9, stagger: 0.06 });
                tl.add(() => {
                    this._applyInnerStroke && this._applyInnerStroke();
                });
                tl.to(allShapes, { fill: '#fff', duration: 0.3, stagger: 0.04 }, '>');
            }
        }

        // 2) Baseline : apparition mot par mot (ou fallback)
        if (this.baseline) {
            tl.to(this.baseline, {
                opacity: 1,
                y: 0,
                duration: 0.6,
                ease: 'back.out(1.4)',
            });
        }

        // 3) Formulaire
        if (this.searchForm)
            tl.to(this.searchForm, { width: '100%', duration: 3, ease: 'back.out(1.7)' }, 0);
    },

    // ---- Helpers ---------------------------------------------------------------
    // Crée un clipPath par path pour obtenir un "stroke interne" cross-browser.
    __ensureInnerStroke(svg) {
        if (!svg) return () => {};
        let defs =
            svg.querySelector('defs') ||
            svg.insertBefore(
                document.createElementNS('http://www.w3.org/2000/svg', 'defs'),
                svg.firstChild
            );

        const maps = new Map();
        const ns = 'http://www.w3.org/2000/svg';
        const shapes = svg.querySelectorAll('path, line, polyline, polygon, rect, circle, ellipse');

        shapes.forEach((el, idx) => {
            if (!(el instanceof SVGElement)) return;
            // id unique si absent
            if (!el.id) el.id = `logo-shape-${idx}`;
            const cpId = `clip-${el.id}`;

            if (!defs.querySelector(`#${cpId}`)) {
                const cp = document.createElementNS(ns, 'clipPath');
                cp.setAttribute('id', cpId);
                const use = document.createElementNS(ns, 'use');
                use.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', `#${el.id}`);
                // Fallback pour SVG2 href
                use.setAttribute('href', `#${el.id}`);
                cp.appendChild(use);
                defs.appendChild(cp);
            }
            maps.set(el, `url(#${cpId})`);
        });
        // Applique le clip-path collecté
        return () => {
            maps.forEach((url, el) => {
                el.setAttribute('clip-path', url);
            });
        };
    },

    // Generic text splitter supporting 'chars', 'words', and 'lines'
    _splitText(el, mode = 'chars') {
        if (!el) return [];
        const text = el.textContent || '';
        if (!text) return [];
        el.setAttribute('aria-label', text); // lecture intacte pour SR
        const frag = document.createDocumentFragment();
        const spans = [];

        if (mode === 'chars') {
            // Split into characters
            for (const char of text) {
                const span = document.createElement('span');
                span.className = 'split-frag';
                span.textContent = char;
                frag.appendChild(span);
                spans.push(span);
            }
        } else if (mode === 'words') {
            // Split into words and keep spaces
            const words = text.split(/(\s+)/);
            words.forEach((w) => {
                const span = document.createElement('span');
                const word = document.createElement('span');
                span.className = 'split-frag';

                word.className = 'split-frag-i';
                word.textContent = w;

                span.appendChild(word);
                frag.appendChild(span);

                spans.push(span);
            });
        } else if (mode === 'lines') {
            // Split into lines by <br>, \n or \r\n
            const lines = text.split(/\r?\n|<br\s*\/?>/i);
            lines.forEach((line, idx) => {
                const span = document.createElement('span');
                span.className = 'split-frag';
                span.textContent = line;
                frag.appendChild(span);
                spans.push(span);
                if (idx < lines.length - 1) {
                    frag.appendChild(document.createElement('br'));
                }
            });
        } else {
            // fallback to chars
            for (const char of text) {
                const span = document.createElement('span');
                span.className = 'split-frag';
                span.textContent = char;
                frag.appendChild(span);
                spans.push(span);
            }
        }

        el.textContent = '';
        el.appendChild(frag);
        return spans;
    },
};
